import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, Shield, Users, FileText, DollarSign, Clock } from 'lucide-react';
import { StructuredData } from '@/components/seo/StructuredData';
import { FAQSchema } from '@/components/seo/FAQSchema';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';

const HealthcarePEO = () => {
  const breadcrumbItems = [
    { name: 'Industries', url: '/industries' },
    { name: 'Healthcare PEO', url: '/industries/healthcare' }
  ];

  const faqData = {
    questions: [
      {
        question: "What makes healthcare PEO services different from regular PEO services?",
        answer: "Healthcare PEO services are specifically designed for medical practices with HIPAA compliance, medical licensing management, and healthcare-specific benefits packages."
      },
      {
        question: "Do healthcare PEOs handle HIPAA compliance?",
        answer: "Yes, specialized healthcare PEOs ensure all HR processes and employee data management comply with HIPAA privacy and security regulations."
      },
      {
        question: "Can PEOs help with medical malpractice insurance?",
        answer: "Many healthcare PEOs offer specialized malpractice coverage and can help coordinate professional liability insurance for medical practices."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-blue-50">
      <Helmet>
        <title>PEO Services for Healthcare & Medical Practices | The PEO App</title>
        <meta name="description" content="Specialized PEO services for healthcare providers, medical practices, and healthcare facilities. HIPAA compliance, medical malpractice, and healthcare-specific HR solutions." />
        <link rel="canonical" href="https://thepeoapp.com/industries/healthcare" />
        <meta name="keywords" content="healthcare PEO, medical practice PEO, PEO for doctors, healthcare HR services, medical practice payroll, HIPAA compliance PEO, healthcare benefits administration" />
        
        <meta property="og:title" content="PEO Services for Healthcare & Medical Practices" />
        <meta property="og:description" content="Specialized PEO services designed for healthcare providers with HIPAA compliance and medical industry expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/healthcare" />
      </Helmet>
      
      <StructuredData 
        type="WebPage" 
        data={{
          name: "Healthcare PEO Services",
          description: "Specialized PEO services for healthcare providers and medical practices",
          url: "https://thepeoapp.com/industries/healthcare"
        }} 
      />
      
      <FAQSchema faqs={faqData.questions} pageUrl="https://thepeoapp.com/industries/healthcare" />
      
      <StructuredData 
        type="BreadcrumbList" 
        data={{
          items: [
            { name: 'Home', url: 'https://thepeoapp.com/' },
            { name: 'Industries', url: 'https://thepeoapp.com/industries' },
            { name: 'Healthcare PEO', url: 'https://thepeoapp.com/industries/healthcare' }
          ]
        }} 
      />
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />
          
          <div className="text-center mb-12">
            <Heart className="h-16 w-16 text-blue-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Healthcare Providers
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services designed specifically for medical practices, 
              healthcare facilities, and healthcare professionals. HIPAA-compliant, industry-expert solutions.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Healthcare Industry Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">HIPAA Compliance Requirements</h3>
                    <p className="text-gray-700">Complex privacy and security regulations affecting all HR processes</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Medical Licensing Management</h3>
                    <p className="text-gray-700">Tracking credentials, renewals, and continuing education requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <DollarSign className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">High Insurance Costs</h3>
                    <p className="text-gray-700">Medical malpractice and specialized healthcare worker coverage</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Staff Shortages & Turnover</h3>
                    <p className="text-gray-700">Difficulty recruiting and retaining qualified healthcare professionals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Complex Scheduling</h3>
                    <p className="text-gray-700">24/7 operations, shift differentials, and on-call requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Regulatory Compliance</h3>
                    <p className="text-gray-700">Joint Commission, CMS, and state healthcare regulations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Healthcare-Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-blue-50 p-6 rounded-xl">
                <Shield className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">HIPAA-Compliant HR</h3>
                <p className="text-gray-700">
                  Complete HR management with built-in HIPAA compliance for all employee data, 
                  background checks, and documentation processes.
                </p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-xl">
                <FileText className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Credentialing Support</h3>
                <p className="text-gray-700">
                  Automated tracking of medical licenses, certifications, continuing education, 
                  and renewal notifications for all healthcare staff.
                </p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-xl">
                <Heart className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Specialized Benefits</h3>
                <p className="text-gray-700">
                  Healthcare-specific benefit packages including malpractice coverage, 
                  continuing education allowances, and wellness programs.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Healthcare-Specialized PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand the unique needs of healthcare organizations 
              and have proven experience in medical practice management.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Healthcare PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Types of Healthcare Organizations We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Medical Practices</h3>
                <p className="text-sm text-gray-600">Private practices, group practices, specialty clinics</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Dental Offices</h3>
                <p className="text-sm text-gray-600">General dentistry, orthodontics, oral surgery</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Healthcare Facilities</h3>
                <p className="text-sm text-gray-600">Urgent care, surgical centers, imaging centers</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Allied Health</h3>
                <p className="text-sm text-gray-600">Physical therapy, mental health, home healthcare</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HealthcarePEO;
